import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import type { PermissionKey } from '@repo/domain';

import { useCan } from '@features/auth/hooks/useCan';
import { useHeldRolesStore } from '@features/auth/stores/useHeldRolesStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  permissions: PermissionKey[];
}

/**
 * Lets the child routes through only for a person holding every listed permission somewhere.
 *
 * While the roles are not known yet — right after a reload — it renders nothing; once they are known,
 * anyone lacking a permission is sent to the account page. It shapes navigation only: the backend
 * still checks every request.
 */
export default function PermissionGuard({ permissions }: Props) {
  const navigate = useNavigate();
  const can = useCan();
  const areHeldRolesKnown = useHeldRolesStore((state) => state.heldRoles !== undefined);

  const isPermitted = permissions.every((permission) => can(permission));

  useEffect(() => {
    if (areHeldRolesKnown && !isPermitted) {
      navigate(ROUTE_PATHS[RouteName.ACCOUNT], { replace: true });
    }
  }, [areHeldRolesKnown, isPermitted, navigate]);

  if (!isPermitted) {
    return null;
  }

  return <Outlet />;
}
